import { useQuery } from '@tanstack/react-query';
import type { Database } from '@/integrations/supabase/types';
import { useOrganization } from '@/hooks/useOrganization';
import {
  listProfiles,
  listProfilesComRoles,
  type ProfileComRole,
} from '@/services/profiles.service';

type AppRole = Database['public']['Enums']['app_role'];

export type { ProfileComRole };

/**
 * Hook genérico para buscar profiles com roles.
 * Role source: `organization_members` (multi-tenant).
 * @param roleFilter - Filtrar por role específica (opcional)
 * @param options.incluirInativos - Incluir membros desativados (default: false)
 */
export function useProfilesComRoles(
  roleFilter?: AppRole | AppRole[],
  options?: { incluirInativos?: boolean },
) {
  const roles = roleFilter
    ? Array.isArray(roleFilter) ? roleFilter : [roleFilter]
    : undefined;
  const incluirInativos = options?.incluirInativos ?? false;
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ['profiles-com-roles', organizationId, roles, incluirInativos],
    enabled: !!organizationId,
    queryFn: () => listProfilesComRoles({ orgId: organizationId!, roles, incluirInativos }),
  });
}

/**
 * Hook para buscar todos os profiles (sem filtro de role).
 * Scoping via RLS policy em `profiles`.
 */
export function useProfiles() {
  const { organizationId } = useOrganization();

  return useQuery({
    queryKey: ['profiles', organizationId],
    queryFn: () => listProfiles(),
  });
}

/**
 * Hook para buscar atendentes.
 * @param incluirInativos - Incluir atendentes desativados (default: false)
 */
export function useAtendentes(incluirInativos = false) {
  return useProfilesComRoles('atendente', { incluirInativos });
}

/**
 * Hook para buscar admins.
 * @param incluirInativos - Incluir admins desativados (default: false)
 */
export function useAdmins(incluirInativos = false) {
  return useProfilesComRoles('admin', { incluirInativos });
}
